/**
 * MultiPartSelector Component
 *
 * Multi-select dropdown for selecting multiple parts with:
 * - Fuzzy search (80% threshold)
 * - Autocomplete from catalog
 * - Free text entry (press Enter to add custom part) WITH VALIDATION
 * - Selected parts as chips
 * - Max 5 parts limit
 * - Part validation against catalog before adding custom parts
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchAvailableParts } from '../hooks/useMultipartSearch';
import { getBackendUrl } from '../utils/api';
import { getApiKeyHeadersAsync } from '../utils/auth';

interface PartValidationResult {
  partName: string;
  isValid: boolean;
  isAutomotivePart: boolean;
  matchedSubAggregate: string | null;
  confidence: number;
  suggestions: string[];
  error: string | null;
}

interface MultiPartSelectorProps {
  selectedParts: string[];
  onPartsChange: (parts: string[]) => void;
  maxParts?: number;
  placeholder?: string;
  disabled?: boolean;
  source?: 'tvs' | 'boodmo';
  isDetecting?: boolean; // Show loading indicator when detecting parts from images
  onValidationError?: (error: string, suggestions: string[]) => void; // Callback for validation errors
}

// Simple fuzzy match function (80% threshold)
function fuzzyMatch(text: string, pattern: string): boolean {
  if (!pattern) return true;

  const textLower = text.toLowerCase();
  const patternLower = pattern.toLowerCase();

  // Exact match
  if (textLower.includes(patternLower)) return true;

  // Simple similarity check
  const patternChars = patternLower.split('');
  let matchCount = 0;
  let textIndex = 0;

  for (const char of patternChars) {
    const foundIndex = textLower.indexOf(char, textIndex);
    if (foundIndex !== -1) {
      matchCount++;
      textIndex = foundIndex + 1;
    }
  }

  const similarity = matchCount / patternLower.length;
  return similarity >= 0.8;
}

export const MultiPartSelector: React.FC<MultiPartSelectorProps> = ({
  selectedParts,
  onPartsChange,
  maxParts = 5,
  placeholder = 'Search and select parts...',
  disabled = false,
  source = 'tvs',
  isDetecting = false,
  onValidationError,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [availableParts, setAvailableParts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationSuggestions, setValidationSuggestions] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load available parts from catalog
  useEffect(() => {
    const loadParts = async () => {
      setIsLoading(true);
      try {
        const parts = await fetchAvailableParts(source, '', 200);
        setAvailableParts(parts);
      } catch (error) {
        console.error('Failed to load parts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadParts();
  }, [source]);

  // Filter parts based on search term with fuzzy matching
  const filteredParts = useMemo(() => {
    if (!searchTerm.trim()) {
      return availableParts.filter((p) => !selectedParts.includes(p)).slice(0, 10);
    }

    return availableParts
      .filter((part) => !selectedParts.includes(part) && fuzzyMatch(part, searchTerm))
      .slice(0, 10);
  }, [availableParts, searchTerm, selectedParts]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Validate a part name against the catalog
  const validatePartName = useCallback(
    async (partName: string): Promise<PartValidationResult | null> => {
      try {
        const backendUrl = getBackendUrl();
        const headers = await getApiKeyHeadersAsync();

        const response = await fetch(`${backendUrl}/api/search/validate-part`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            partNames: [partName],
            source: source,
          }),
        });

        if (!response.ok) {
          console.error('Part validation API error:', response.status);
          return null;
        }

        const data = await response.json();
        if (data.success && data.results && data.results.length > 0) {
          return data.results[0];
        }

        return null;
      } catch (error) {
        console.error('Part validation error:', error);
        return null;
      }
    },
    [source]
  );

  // Add a part (with validation for custom parts)
  const addPart = useCallback(
    async (part: string, isFromCatalog: boolean = false) => {
      const trimmedPart = part.trim().toUpperCase();
      if (!trimmedPart) return;
      if (selectedParts.length >= maxParts) return;
      if (selectedParts.includes(trimmedPart)) return;

      // Clear any previous validation error
      setValidationError(null);
      setValidationSuggestions([]);

      // If the part is from the catalog dropdown, no need to validate
      if (isFromCatalog) {
        onPartsChange([...selectedParts, trimmedPart]);
        setSearchTerm('');
        setHighlightedIndex(-1);
        setIsOpen(false);
        return;
      }

      // For custom parts, validate against the catalog
      setIsValidating(true);
      try {
        const validationResult = await validatePartName(trimmedPart);

        if (validationResult) {
          if (validationResult.isValid) {
            // Valid part - use matched subAggregate if available
            const partToAdd = validationResult.matchedSubAggregate || trimmedPart;
            onPartsChange([...selectedParts, partToAdd]);
            setSearchTerm('');
            setHighlightedIndex(-1);
            setIsOpen(false);
          } else {
            // Invalid part - show error
            const errorMsg = validationResult.error ||
              `'${trimmedPart}' is not recognized as a valid automotive part.`;
            setValidationError(errorMsg);
            setValidationSuggestions(validationResult.suggestions || []);

            // Notify parent component if callback provided
            if (onValidationError) {
              onValidationError(errorMsg, validationResult.suggestions || []);
            }
          }
        } else {
          // Validation API failed - allow adding but warn
          console.warn('Part validation API returned no result, allowing part');
          onPartsChange([...selectedParts, trimmedPart]);
          setSearchTerm('');
          setHighlightedIndex(-1);
          setIsOpen(false);
        }
      } catch (error) {
        console.error('Part validation error:', error);
        // On error, allow adding to not block user
        onPartsChange([...selectedParts, trimmedPart]);
        setSearchTerm('');
        setHighlightedIndex(-1);
        setIsOpen(false);
      } finally {
        setIsValidating(false);
      }
    },
    [selectedParts, onPartsChange, maxParts, validatePartName, onValidationError]
  );

  // Remove a part
  const removePart = useCallback(
    (partToRemove: string) => {
      onPartsChange(selectedParts.filter((p) => p !== partToRemove));
    },
    [selectedParts, onPartsChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          Math.min(prev + 1, filteredParts.length - 1)
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredParts.length) {
          // Selected from catalog - no validation needed
          addPart(filteredParts[highlightedIndex], true);
        } else if (searchTerm.trim()) {
          // Custom part - needs validation
          addPart(searchTerm, false);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setValidationError(null);
        setValidationSuggestions([]);
      } else if (e.key === 'Backspace' && !searchTerm && selectedParts.length > 0) {
        // Remove last part on backspace if input is empty
        removePart(selectedParts[selectedParts.length - 1]);
      }
    },
    [filteredParts, highlightedIndex, addPart, searchTerm, selectedParts, removePart]
  );

  const canAddMore = selectedParts.length < maxParts;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Selected Parts Chips + Input */}
      <div
        className={`flex flex-wrap gap-2 p-3 border-2 rounded-xl bg-white min-h-[50px] items-center transition-all duration-200 ${
          isOpen ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'
        } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''} ${isDetecting ? 'border-blue-300' : ''}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {/* Detection Loading Indicator */}
        {isDetecting && (
          <svg className="w-5 h-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {/* Selected Part Chips */}
        {selectedParts.map((part) => (
          <div
            key={part}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
          >
            <span className="leading-none">{part}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removePart(part);
                }}
                className="inline-flex items-center justify-center w-4 h-4 bg-blue-300 hover:bg-blue-400 text-blue-800 rounded-full transition-colors ml-0.5"
                aria-label={`Remove ${part}`}
              >
                <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 2L8 8M8 2L2 8" />
                </svg>
              </button>
            )}
          </div>
        ))}

        {/* Input Field */}
        {canAddMore && (
          <input
            ref={inputRef}
            type="text"
            className="flex-1 min-w-[150px] border-none outline-none text-base bg-transparent"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
              setHighlightedIndex(-1);
              // Clear validation error when user starts typing again
              if (validationError) {
                setValidationError(null);
                setValidationSuggestions([]);
              }
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selectedParts.length === 0 ? placeholder : 'Add more parts...'}
            disabled={disabled || isValidating}
          />
        )}

        {/* Max reached indicator */}
        {!canAddMore && (
          <span className="text-sm text-gray-500 italic">Maximum {maxParts} parts</span>
        )}
      </div>

      {/* Part Count */}
      {selectedParts.length > 0 && (
        <div className="text-xs text-gray-500 mt-1 text-right">
          {selectedParts.length}/{maxParts} parts selected
        </div>
      )}

      {/* Validation Error Message */}
      {validationError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{validationError}</p>
          {validationSuggestions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-red-600 mb-1">Did you mean:</p>
              <div className="flex flex-wrap gap-1">
                {validationSuggestions.slice(0, 5).map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      setValidationError(null);
                      setValidationSuggestions([]);
                      addPart(suggestion, true);
                    }}
                    className="px-2 py-1 bg-white border border-red-300 rounded text-xs text-red-700 hover:bg-red-100 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && canAddMore && !disabled && (
        <div className="absolute top-full left-0 right-0 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-[250px] overflow-y-auto z-50 mt-1">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              Loading parts...
            </div>
          ) : isValidating ? (
            <div className="p-4 text-center text-blue-600 text-sm flex items-center justify-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Validating part...
            </div>
          ) : filteredParts.length > 0 ? (
            <ul className="py-1">
              {filteredParts.map((part, index) => (
                <li
                  key={part}
                  className={`px-4 py-2.5 cursor-pointer text-sm transition-colors ${
                    index === highlightedIndex
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => addPart(part, true)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {part}
                </li>
              ))}
            </ul>
          ) : searchTerm.trim() ? (
            <div className="p-3">
              <button
                type="button"
                onClick={() => addPart(searchTerm, false)}
                disabled={isValidating}
                className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors text-left disabled:opacity-50"
              >
                {isValidating ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Validating...
                  </span>
                ) : (
                  <>
                    Add "<span className="font-semibold">{searchTerm.toUpperCase()}</span>" as custom part
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 mt-1 px-1">
                Custom parts will be validated against our catalog
              </p>
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              Type to search or add custom part
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiPartSelector;
